import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import TableInstance from '@/shared/constants/tableInstance';
import Queue from '@/shared/types/fulfilment/queue.type';

import Package from '../PackagingDetailsTableForm_v2/types/package.type';
import CustomColumn from './components/CustomColumn';
import useCustomsProductTableForm from './hooks/useCustomsProductTableForm';
import ProductTableRow from './types/productTableRow.type';

type CustomsProductTableFormProps = {
  selectedQueues: Queue[];
  packages: Package[];
  handleAssignItemsToPackage: (value: {
    packageId: string;
    assignedItems: number;
  }) => void;
  onAddPackage: () => void;
  getProductTableData: (data: ProductTableRow[]) => void;
};

const CustomsProductTableForm: FC<CustomsProductTableFormProps> = ({
  selectedQueues,
  packages,
  handleAssignItemsToPackage,
  onAddPackage,
  getProductTableData,
}) => {
  const { t } = useTranslation(['forms']);

  const {
    model,
    table,
    apiRef,
    columns,
    visibilityModel,
    setVisibilityModel,
    handleUpdateTableRow,
  } = useCustomsProductTableForm({
    packages,
    selectedQueues,
    handleAssignItemsToPackage,
    getProductTableData,
  });

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'variant_id':
      case 'product_name':
      case 'origin':
      case 'unit_price':
      case 'weight':
      case 'product_description':
      case 'assign_package':
        return {
          ...column,
          filterable: false,
          sortable: false,
          groupable: false,
          disableReorder: true,
          disableColumnMenu: true,
          disableExport: true,
          renderHeader: () => (
            <Typography variant='body2' color='text.secondary'>
              {t(`forms:${column.field}`)}
            </Typography>
          ),
          renderCell(params) {
            return (
              <CustomColumn
                cellParams={params}
                columnName={params.field}
                packages={packages}
                handleUpdateTableRow={handleUpdateTableRow}
              />
            );
          },
        };
      default:
        return column;
    }
  });

  return (
    <div style={{ height: '50vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.fulfilment_queue_id as string}
        columns={customizedColumns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        checkboxSelection={false}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        customProps={{
          tableInstance: TableInstance.Products,
          apiRef,
          model,
          toolbar: {
            toolbarActions: (
              <>
                <Button variant='contained' size='large' onClick={onAddPackage}>
                  Add Package
                </Button>
              </>
            ),
          },
        }}
        getRowSpacing={() => ({
          top: 4,
          bottom: 4,
        })}
      />
    </div>
  );
};

export default CustomsProductTableForm;
